import { Slider } from "@mui/material";
import { showAlert } from "components/Common/commonFunctions";
import RightPanelCard from "components/Common/RightPanelCard";
import CommonTable from "components/ReactTable/Table";
import React, { useEffect, useRef, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  CardTitle,
  Col,
  Container,
  FormGroup,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Label,
  Row,
  UncontrolledTooltip,
} from "reactstrap";
import Select, { components } from "react-select";
import {
  fixedWidthCustomStyle,
  sliderStyles,
  MASTER_STATUS_OPTIONS,
  DEFAULT_RECORDS_PER_PAGE,
} from "components/Common/constants";
import NotificationAlert from "react-notification-alert";
import { useDispatch, useSelector } from "react-redux";
import { getServiceList, editService, addService } from "actions/services";
import { servicesList } from "constants/services";
import { REGEX } from "components/Common/constants";
import { exportServicesCSV } from "actions/services";
import moment from "moment";

const initialFields = {
  title: { value: "", error: "" },
  description: { value: "", error: "" },
  charges: { value: "1", error: "" },
  status: { value: "ACTIVE", error: "" },
};

function ServicesList() {
  const dispatch = useDispatch();
  const notificationAlertRef = useRef(null);
  const serviceListRes = useSelector((state) => state.service.servicesList);

  const [pageNum, setPageNum] = useState(1);
  const formattedData =
    serviceListRes?.data?.Service?.map((item, index) => {
      return {
        ...item,
        srNo:
          (pageNum - 1) *
          (serviceListRes?.data?.noOfRecordsPerPage ||
            DEFAULT_RECORDS_PER_PAGE) +
          index +
          1,
      };
    }) || [];

  const [serviceListState, setServiceListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
  });
  const [serviceCard, setServiceCard] = useState({ open: false, mode: "Add" });
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState(initialFields);
  const [showLoader, setShowLoader] = useState(false);
  const [showLoaderOnStatus, setShowLoaderOnStatus] = useState({});
  const [filterState, setFilterState] = useState({
    searchName: "",
    searchStatus: null,
    priceRange: [0, 10000],
    filterParams: [],
  });
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    getServiceList(
      dispatch,
      pageNum,
      filterState.filterParams?.filters,
      filterState.filterParams?.searchString,
    );
    return () => {
      dispatch({ type: servicesList, payload: {} });
    };
  }, [dispatch, pageNum, filterState.filterParams]);

  useEffect(() => {
    if (serviceListRes?.code === 200) {
      setServiceListState({
        isLoading: false,
        error: "",
        noRecords: serviceListRes?.data?.Service?.length === 0,
        showLoaderOnClick: false,
      });
    } else if (serviceListRes?.code || serviceListRes?.message) {
      setServiceListState({
        isLoading: false,
        error:
          serviceListRes?.message ||
          "Failed to load service list! Please try again.",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [serviceListRes]);

  const columns = [
  { 
    Header: () => <span style={{ textAlign: 'center' }}>SR. NO.</span>,
    accessor: "srNo", 
    className: "col-srNo",
    centerAlign:true,
    width: 60,
    minWidth: 60,
    maxWidth: 80
  },
  {
    Header: () => <div style={{ textAlign: 'left' }}>TITLE</div>,
    accessor: "title",
    className: "service-title",
    width: 180,
    minWidth: 140,
    maxWidth: 220,
    Cell: ({ value }) => (
      <div style={{ 
        whiteSpace: 'nowrap', 
        overflow: 'hidden', 
        textOverflow: 'ellipsis' 
      }}>
        {value}
      </div>
    )
  },
  {
    Header: () => <div style={{ textAlign: 'left' }}>DESCRIPTION</div>,
    accessor: "description",
    className: "col-desc",
    width: 250,
    minWidth: 200,
    maxWidth: 350,
    Cell: ({ value }) => (
      <div style={{ 
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        maxHeight: '60px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical'
      }}>
        {value}
      </div>
    ),
  },
  {
    Header: () => <div style={{ textAlign: 'right', paddingRight: '20px' }}>CHARGES (SAR)</div>,
    accessor: "charges",
    className: "service-charges",
    width: 140,
    minWidth: 120,
    maxWidth: 180,
    Cell: ({ value }) => (
      <div style={{ 
        textAlign: 'center',
        fontWeight: '500',
        paddingRight: '20px'
      }}>
        {value}
      </div>
    ),
  },
  {
    Header: () => <div style={{ textAlign: 'center' }}>STATUS</div>,
    accessor: "status",
    className: "service-status",
    width: 120,
    minWidth: 100,
    maxWidth: 150,
    Cell: ({ value }) => (
      <div style={{ textAlign: 'left' }}>
        <Badge color={value === "ACTIVE" ? "success" : "warning"} pill>
          {value}
        </Badge>
      </div>
    ),
  },
  {
    Header: () => <div style={{ textAlign: 'right' }}>ACTIONS</div>,
    accessor: "actions",
    className: "service-action",
    width: 130,
    minWidth: 110,
    maxWidth: 160,
    disableSortBy: true,
    Cell: ({ row }) => (
      <div className="actions-right d-flex justify-content-center" style={{ gap: '4px' }}>
        <Button
          className="btn-icon icon-info"
          color="link"
          id={`edit${row.original.ID}`}
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            handleEditService(row.original);
          }}
        >
          <i className="tim-icons icon-pencil tim-md" />
        </Button>
        <UncontrolledTooltip delay={0} target={`edit${row.original.ID}`}>
          Edit
        </UncontrolledTooltip>
        {showLoaderOnStatus[row.original.ID] ? (
          <span className="d-flex align-items-center ml-1">
            <i className="fa fa-spinner fa-spin search-loader" />
          </span>
        ) : (
          <>
            <Button
              onClick={() => handleStatusToggle(row.original)}
              color="link"
              size="sm"
              className="btn-icon btn-neutral ml-1"
              id={`status${row.original.ID}`}
              disabled={!!showLoaderOnStatus[row.original.ID]}
            >
              {row.original.status === "ACTIVE" ? (
                <i className="fa fa-xmark custom-icon btn-icon icon-danger" />
              ) : (
                <i className="fa fa-check custom-icon btn-icon icon-success" />
              )}
            </Button>
            <UncontrolledTooltip
              delay={0}
              target={`status${row.original.ID}`}
            >
              {row.original.status === "ACTIVE" ? "Inactivate" : "Activate"} Service
            </UncontrolledTooltip>
          </>
        )}
      </div>
    ),
  },
];

  const confirmStatus = async (data, isActive) => {
    try {
      setShowLoaderOnStatus((prev) => ({ ...prev, [data.ID]: true }));
      const newStatus = isActive ? "INACTIVE" : "ACTIVE";

      const res = await editService(data.ID, {
        ID: data.ID,
        status: newStatus,
      });

      if (res?.code === 200) {
        notify("success", res.message || "Service status updated");
        // Refresh list
        getServiceList(
          dispatch,
          pageNum,
          JSON.stringify(filterState.filterParams),
          filterState.filterParams.searchString,
        );
      } else {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message || "Failed to update status!";
        notify("danger", message);
      }
    } catch (e) {
      notify("danger", "Failed to update status!");
    } finally {
      setShowLoaderOnStatus((prev) => ({ ...prev, [data.ID]: false }));
    }
  };

  const handleStatusToggle = async (data) => {
    const isActive = data.status === "ACTIVE";
    showAlert({
      title: `Are you sure you want to ${isActive ? "inactivate" : "activate"
        } this service?`,
      message: `Service titled "${data.title}" will become ${isActive ? "inactive" : "active"
        }.`,
      type: "warning",
      confirmStyle: "default",
      cancelStyle: "secondary",
      confirmText: "Yes",
      cancelText: "Cancel",
      onConfirm: () => confirmStatus(data, isActive),
      onCancel: () => { },
    });
  };

  const notify = (type, message) => {
    const options = {
      place: "tr",
      message: <div>{message}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    };
    notificationAlertRef.current.notificationAlert(options);
  };

  const handleInputChange = (e) => {
    setFormData((p) => ({
      ...p,
      [e.target.name]: {
        value: e.target.type === "checkbox" ? e.target.checked : e.target.value,
        error: "",
      },
    }));
  };

  const buildFilterParams = () => {
    const filterParams = [];
    if (filterState.searchStatus) {
      filterParams.push({
        field: "Status",
        condition: "eq",
        filterValues: [filterState.searchStatus.value],
      });
    }
    if (
      filterState.priceRange &&
      (filterState.priceRange[0] !== 0 || filterState.priceRange[1] !== 10000)
    ) {
      filterParams.push({
        field: "Charges",
        condition: "btw",
        filterValues: [
          String(filterState.priceRange[0]),
          String(filterState.priceRange[1]),
        ],
      });
    }

    return filterParams.length > 0 ? JSON.stringify(filterParams) : [];
  };

  const handleSearch = () => {
    const searchString = filterState.searchName.trim();
    if (
      !searchString &&
      (!filterState.searchStatus || filterState.searchStatus === null) &&
      filterState.priceRange[0] === 0 &&
      filterState.priceRange[1] === 10000
    ) {
      // All filters are empty — reset the applied filter (same as Clear)
      const isEmptyParams =
        !filterState.filterParams?.filters?.length &&
        !filterState.filterParams?.searchString;
      if (!isEmptyParams) {
        handleClear();
      }
      return;
    }
    const newParams = {
      filters: buildFilterParams(),
      searchString,
    };
    if (
      JSON.stringify(filterState.filterParams) !== JSON.stringify(newParams)
    ) {
      setServiceListState((prev) => ({
        ...prev,
        isLoading: true,
        showLoaderOnClick: true,
      }));
      setFilterState((prev) => ({
        ...prev,
        filterParams: newParams,
      }));
    }
    setFilterState((prev) => ({ ...prev, filterParams: newParams }));

    if (pageNum !== 1) setPageNum(1);
  };

  const handleClear = () => {
    setServiceListState((prev) => ({
      ...prev,
      isLoading: true,
    }));
    setFilterState({
      searchName: "",
      searchStatus: null,
      priceRange: [0, 10000],
      filterParams: [],
    });
    if (pageNum !== 1) setPageNum(1);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const resetForm = () => {
    setFormData(initialFields);
  };

  // based on the field name will retuen error if condition meets
  const getValidationError = (name, value) => {
    switch (name) {
      case "title":
        return !value ? "Please enter a title" : "";
      case "charges":
        if (value === "") return "Please enter charges";
        if (Number(value) <= 0) return "Enter number greater than 0";
        return "";

      case "description":
        return !value ? "Please enter a description" : "";
      default:
        return "";
    }
  };

  const handleChargesChange = (e) => {
    const raw = e.target.value;
    if (raw === "" || REGEX.ONLY_NUMBERS_REGEX.test(raw)) {
      const normalized = raw.startsWith(".") ? `0${raw}` : raw;
      setFormData((prev) => ({
        ...prev,
        charges: { ...prev.charges, value: normalized, error: "" },
      }));
    }
  };

  const validateForm = () => {
    let newUser = { ...formData };

    Object.keys(newUser).forEach((key) => {
      newUser[key] = {
        ...newUser[key],
        error: getValidationError(key, newUser[key].value),
      };
    });

    setFormData(newUser); // set the error state

    return Object.values(newUser).every((item) => item.error === "");
  };

  const handleOnBlur = (name, value) => {
    const trimmed = typeof value === "string" ? value.trim() : value;
    const error = getValidationError(name, trimmed);
    setFormData((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value: trimmed,
        error,
      },
    }));
  };

  const toggleModal = (mode = "Add", user = null) => {
    if (serviceCard.open) {
      resetForm();
      setEditingUser(null);
      setServiceCard({ open: false, mode: "Add" });
    } else {
      if (mode === "Edit" && user) {
        setEditingUser(user);
        setFormData({
          jobTypeName: { value: user.name || "", error: "" },
          status: {
            value: user.status === "ACTIVE" ? "ACTIVE" : "INACTIVE",
            error: "",
          },
        });
      } else {
        resetForm();
      }
      setServiceCard({ open: true, mode });
    }
  };

  // Add submit -> call add Service API
  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      setShowLoader(true);
      const bodyData = {
        title: formData.title.value,
        description: formData.description.value,
        charges: parseFloat(formData.charges.value),
        status: formData.status.value,
      };
      const res = await addService(bodyData);
      if (res?.code === 200) {
        notify("success", res.message || "Service added successfully");
        toggleModal();
        if (pageNum === 1)
          getServiceList(
            dispatch,
            pageNum,
            filterState.filterParams,
            filterState.filterParams.searchString,
          );
        else setPageNum(1);
      } else {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg || JSON.stringify(res?.message)
          : res?.message || "Failed to add service!";
        notify("danger", message);
      }
    } catch (err) {
      notify("danger", "Failed to add service!");
    } finally {
      setShowLoader(false);
    }
  };

  // Edit submit -> call edit Service API
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setShowLoader(true);
      const bodyData = {
        title: formData.title.value,
        description: formData.description.value,
        charges: parseFloat(formData.charges.value),
        status: formData.status.value,
      };

      const res = await editService(editingUser.ID, bodyData);
      if (res?.code === 200) {
        notify("success", res.message || "Service updated successfully");
        handleCloseModal();
        getServiceList(
          dispatch,
          pageNum,
          filterState.filterParams,
          filterState.filterParams.searchString,
        );
      } else {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg || JSON.stringify(res?.message)
          : res?.message || "Failed to update service!";
        notify("danger", message);
      }
    } catch (err) {
      notify("danger", "Failed to update service!");
    } finally {
      setShowLoader(false);
    }
  };

  const handleAddService = () => {
    setServiceCard({ open: true, mode: "Add" });
  };

  const handleCloseModal = () => {
    setServiceCard({ open: false, mode: null });
    setFormData(initialFields);
    setEditingUser(null);
  };

  const handleEditService = (data) => {
    setEditingUser(data);
    setServiceCard({ open: true, mode: "Edit" });
    setFormData({
      title: { value: data.title, error: "" },
      description: { value: data.description, error: "" },
      charges: { value: data.charges, error: "" },
      status: { value: data.status || "ACTIVE", error: "" },
    });
  };

  const Option = (props) => {
    return (
      <components.Option {...props}>
        <Badge
          color={props.data.value === "ACTIVE" ? "success" : "warning"}
          pill
        >
          {props.data.label}
        </Badge>
      </components.Option>
    );
  };

  const SingleValue = (props) => {
    return (
      <components.SingleValue {...props}>
        <Badge
          color={props.data.value === "ACTIVE" ? "success" : "warning"}
          pill
        >
          {props.data.label}
        </Badge>
      </components.SingleValue>
    );
  };

  const handleExportCsv = async () => {
    try {
      setIsDownloading(true);
      const res = await exportServicesCSV({
        filters: filterState.filterParams.filters,
        searchString: filterState.filterParams.searchString,
      });
      if (res.status === 200) {
        const blob = new Blob(["\uFEFF" + res?.data], {
          type: "text/csv;charset=utf-8;",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.download = `services-report_${moment().format("YYYY-MM-DD")}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        notify("success", res?.message || "CSV exported successfully");
      } else {
        const message = Array.isArray(res?.message || res?.data?.message)
          ? res?.message[0]?.Msg || res?.data?.message[0]?.Msg
          : res?.message || res?.data?.message || "Failed to export csv, try again later";
        notify("danger", message);
      }
    } catch (error) {
      notify("danger", error?.message || "Something went wrong");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      {isDownloading && (
        <div className="loading-overlay">
          <div className="loading-card">
            <i className="fa fa-spinner fa-spin fa-2x text-deep-blue mb-3" />
            <div className="font-weight-bold text-dark">
              Processing… Please wait.
            </div>
          </div>
        </div>
      )}
      <div className="rna-container">
        <NotificationAlert ref={notificationAlertRef} />
      </div>

      <Container fluid className="mt-4">
        <Row>
          <Col className="mb-5" md="12">
            <Card>
              <CardHeader className="d-flex align-items-center justify-content-between mb-2">
                <CardTitle tag="h3" className="font-weight-normal">
                  Services
                </CardTitle>
                <div className="d-flex align-items-center">
                  <Button
                    className="btn-simple btn-round btn-style mr-3"
                    color="default"
                    size="md"
                    onClick={handleExportCsv}
                  >
                    <i className="fas fa-arrow-up-from-bracket mr-1" /> Export
                    Services
                  </Button>
                  <Button
                    className="btn-round btn-style"
                    color="default"
                    size="md"
                    onClick={handleAddService}
                  >
                    <i className="fa fa-plus mr-1" />
                    Add Service
                  </Button>
                </div>
              </CardHeader>
              <CardBody className="services-card-body">
                <div className="d-flex flex-wrap align-items-center justify-content-start filter-group">
                  <Input
                    type="text"
                    maxLength={150}
                    placeholder="Search by title or description"
                    className="mr-2 mb-2 filter-input-large"
                    value={filterState.searchName}
                    onChange={(e) =>
                      setFilterState((prev) => ({
                        ...prev,
                        searchName: e.target.value,
                      }))
                    }
                    onKeyDown={handleKeyDown}
                  />
                  <Select
                    className="mr-2 mb-2 filter-select"
                    options={MASTER_STATUS_OPTIONS}
                    placeholder="Status"
                    value={filterState.searchStatus}
                    onChange={(selected) =>
                      setFilterState((prev) => ({
                        ...prev,
                        searchStatus: selected,
                      }))
                    }
                    classNamePrefix="react-select"
                    styles={fixedWidthCustomStyle()}
                    components={{ Option, SingleValue }}
                    onKeyDown={handleKeyDown}
                  />

                  <div className="mx-4 slider-style">
                    <Row className="align-items-center">
                      <Col xs="6">
                        <Label className="text-muted">Service Charges</Label>
                      </Col>
                      <Col xs="6" className="text-right">
                        <Label className="text-muted">
                          SAR (
                          {filterState.priceRange[0] === 10000
                            ? `${filterState.priceRange[0]} - Above`
                            : `${filterState.priceRange[0]} - ${filterState.priceRange[1]}`}
                          )
                        </Label>
                      </Col>
                    </Row>

                    <Row>
                      <Col xs="6">
                        <Slider
                          size="small"
                          value={filterState.priceRange}
                          onChange={(e, newValue) => {
                            if (newValue[0] === 10000) {
                              newValue[1] = 100000;
                            }
                            setFilterState({
                              ...filterState,
                              priceRange: newValue,
                            });
                          }}
                          onKeyDown={handleKeyDown}
                          min={0}
                          max={10000}
                          step={100}
                          marks={[
                            { value: 0, label: "0" },
                            { value: 2500, label: "2.5k" },
                            { value: 5000, label: "5k" },
                            { value: 7500, label: "7.5k" },
                            { value: 10000, label: "10k" },
                          ]}
                          sx={sliderStyles}
                        />
                      </Col>
                    </Row>
                  </div>

                  <Button
                    color="default"
                    className="d-flex align-items-center justify-content-center mb-3 filter-btn btn-simple mt-2 mr-1"
                    onClick={handleSearch}
                  >
                    {serviceListState.showLoaderOnClick ? (
                      <i className="fa fa-spinner fa-spin search-loader" />
                    ) : (
                      <>
                        <i className="fa fa-search mr-1" />
                        Search
                      </>
                    )}
                  </Button>
                  {Object.keys(filterState.filterParams).length > 0 && (
                    <Button
                      color="default"
                      className="mb-3 filter-btn btn-simple mt-2"
                      onClick={handleClear}
                    >
                      <i className="fa fa-times mr-1" /> Clear
                    </Button>
                  )}
                </div>

                <div className="mt-3 mb-4 mx-1">
                  {serviceListRes?.data?.totalRecords ? (
                    filterState.filterParams &&
                      Object.keys(filterState.filterParams).length > 0 ? (
                      <span className="font-weight-bold">
                        Showing {serviceListRes?.data?.totalFilteredRecords} of{" "}
                        {serviceListRes?.data?.totalRecords} Service
                      </span>
                    ) : (
                      <span className="font-weight-bold">
                        Total Services: {serviceListRes?.data?.totalRecords}
                      </span>
                    )
                  ) : null}
                </div>

                {serviceListState.isLoading ? (
                  <div className="loaderContainer">
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </div>
                ) : serviceListState.error ? (
                  <div className="loaderContainer">
                    {serviceListState.error}
                  </div>
                ) : serviceListState.noRecords ? (
                  <div className="loaderContainer text-muted">
                    No Services added yet.
                  </div>
                ) : (
                  <div className="table-responsive">
                    <CommonTable
                      className="-striped -highlight table-hover"
                      data={formattedData}
                      resizable={false}
                      columns={columns}
                      defaultPageSize={DEFAULT_RECORDS_PER_PAGE}
                      activePage={pageNum}
                      showPaginationBottom={true}
                      setPageNum={setPageNum}
                      totalPages={Math.ceil(
                        serviceListRes?.data?.totalFilteredRecords /
                        serviceListRes?.data?.noOfRecordsPerPage ||
                        DEFAULT_RECORDS_PER_PAGE,
                      )}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <RightPanelCard isOpen={serviceCard.open} onClose={handleCloseModal}>
        <Card className="mb-0">
          <CardHeader
            className="d-flex align-items-center justify-content-between mb-2 flex-shrink-0"
            tag="h4"
            toggle={handleCloseModal}
          >
            {serviceCard.mode} Service
            <i
              className="fa fa-close text-muted pointer"
              onClick={handleCloseModal}
            />
          </CardHeader>

          <CardBody>
            <Row>
              <Col md="6" className="mb-3">
                <FormGroup
                  className={`has-label ${formData.title.error ? "has-danger" : ""
                    } d-flex flex-column align-items-start`}
                >
                  <Label>
                    Title <span className="validation-error">*</span>
                  </Label>
                  <Input
                    name="title"
                    type="text"
                    placeholder="Enter title"
                    autoComplete="off"
                    maxLength={100}
                    value={formData.title.value}
                    onChange={handleInputChange}
                    onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                    required
                  />
                  {formData.title.error && (
                    <small className="validation-error">
                      {formData.title.error}
                    </small>
                  )}
                </FormGroup>
              </Col>
              <Col md="6" className="mb-2">
                <FormGroup
                  className={`has-label ${formData.charges.error ? "has-danger" : ""
                    } d-flex flex-column align-items-start`}
                >
                  <Label>
                    Charges <span className="validation-error">*</span>
                  </Label>
                  <InputGroup className="input-group">
                    <InputGroupAddon addonType="prepend">
                      <InputGroupText className="custom-border d-flex align-items-center text-muted">
                        SAR
                      </InputGroupText>
                    </InputGroupAddon>
                    <Input
                      name="charges"
                      type="text"
                      placeholder="Enter charges"
                      autoComplete="off"
                      minLength={1}
                      maxLength={7}
                      value={formData.charges.value}
                      onChange={handleChargesChange}
                      onBlur={(e) =>
                        handleOnBlur(e.target.name, e.target.value)
                      }
                      required
                    />
                  </InputGroup>
                  {formData.charges.error && (
                    <small className="validation-error">
                      {formData.charges.error}
                    </small>
                  )}
                </FormGroup>
              </Col>
              <Col md={serviceCard.mode == "Add" ? "6" : "12"} className="mb-3">
                <FormGroup
                  className={`has-label ${formData.description.error ? "has-danger" : ""
                    } d-flex flex-column align-items-start`}
                >
                  <Label>
                    Description <span className="validation-error">*</span>
                  </Label>

                  <Input
                    name="description"
                    type="textarea"
                    placeholder="Enter description"
                    autoComplete="off"
                    maxLength={500}
                    value={formData.description.value}
                    onChange={handleInputChange}
                    onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                    required
                  />
                  {formData.description.error && (
                    <small className="validation-error">
                      {formData.description.error}
                    </small>
                  )}
                </FormGroup>
              </Col>
              {serviceCard.mode == "Add" ? (
                <Col md="6">
                  <FormGroup>
                    <Label>
                      Status <span className="validation-error">*</span>
                    </Label>
                    <div className="radio-rapper">
                      <FormGroup check inline>
                        <Label check>
                          <Input
                            type="radio"
                            name="status"
                            value="ACTIVE"
                            checked={formData.status.value === "ACTIVE"}
                            onChange={handleInputChange}
                          />{" "}
                          Active
                        </Label>
                      </FormGroup>
                      <FormGroup check inline>
                        <Label check>
                          <Input
                            type="radio"
                            name="status"
                            value="INACTIVE"
                            checked={formData.status.value === "INACTIVE"}
                            onChange={handleInputChange}
                          />{" "}
                          Inactive
                        </Label>
                      </FormGroup>
                    </div>
                  </FormGroup>
                </Col>
              ) : null}
            </Row>
          </CardBody>

          <CardFooter className="d-flex justify-content-end mb-0">
            <Button
              color="secondary"
              onClick={handleCloseModal}
              className="mr-2"
            >
              Cancel
            </Button>
            <Button
              color="default"
              type="submit"
              onClick={
                serviceCard.mode === "Add" ? handleAddSubmit : handleEditSubmit
              }
              disabled={Object.values(formData).some(
                (item) => item.error !== "",
              )}
            >
              {showLoader ? (
                <i className="fa fa-spinner fa-spin fa-lg text-white" />
              ) : (
                "Save"
              )}
            </Button>
          </CardFooter>
        </Card>
      </RightPanelCard>
    </>
  );
}

export default ServicesList;
